import React, { useEffect, useState, useRef, useCallback } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";
import { TrialClassType } from "../../interfaces/trial-class.interface";
import { whenCometChatCallsReady } from "../../services/cometChat.service";

interface ConferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  trialClass: TrialClassType | null;
}

type CallsUser = {
  uid?: string;
  getUid?: () => string;
};

const getUid = (user: CallsUser | null | undefined): string =>
  String(user?.uid ?? user?.getUid?.() ?? "");

const leaveSessionSafe = () => {
  try {
    if (typeof CometChatCalls.leaveSession === "function") {
      CometChatCalls.leaveSession();
      return;
    }
  } catch {
    // fall through
  }
  try {
    (CometChatCalls as { endSession?: () => void }).endSession?.();
  } catch {
    // Session may already be ended.
  }
};

const ConferenceModal: React.FC<ConferenceModalProps> = ({
  isOpen,
  onClose,
  trialClass,
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const callContainerRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);
  const containerReadyRef = useRef(false);
  const isClosingRef = useRef(false);
  const eventCleanupsRef = useRef<Array<() => void>>([]);

  const clearEventListeners = useCallback(() => {
    eventCleanupsRef.current.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore
      }
    });
    eventCleanupsRef.current = [];
  }, []);

  const cleanupSession = useCallback(() => {
    clearEventListeners();
    leaveSessionSafe();
    hasStartedRef.current = false;
    containerReadyRef.current = false;
  }, [clearEventListeners]);

  const endAndClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    cleanupSession();
    onClose();
  }, [cleanupSession, onClose]);

  const addCallsListener = (event: string, handler: (...args: unknown[]) => void) => {
    const sdk = CometChatCalls as {
      addEventListener?: (
        event: string,
        handler: (...args: unknown[]) => void
      ) => (() => void) | void;
      removeEventListener?: (
        event: string,
        handler: (...args: unknown[]) => void
      ) => void;
    };
    if (typeof sdk.addEventListener !== "function") return;
    const cleanup = sdk.addEventListener(event, handler);
    if (typeof cleanup === "function") {
      eventCleanupsRef.current.push(cleanup);
    } else {
      eventCleanupsRef.current.push(() => {
        sdk.removeEventListener?.(event, handler);
      });
    }
  };

  const startCallSession = useCallback(async () => {
    if (!trialClass?._id || !callContainerRef.current || hasStartedRef.current) {
      console.log("Cannot start session:", {
        hasTrialClass: !!trialClass?._id,
        hasContainer: !!callContainerRef.current,
        hasStarted: hasStartedRef.current,
      });
      return;
    }

    hasStartedRef.current = true;
    isClosingRef.current = false;
    setIsStarting(true);
    setError(null);

    try {
      await whenCometChatCallsReady();

      const loggedInUser = (await CometChatCalls.getLoggedInUser()) as CallsUser | null;
      if (!loggedInUser || !getUid(loggedInUser)) {
        setError("User not logged in to CometChat Calls");
        setIsStarting(false);
        hasStartedRef.current = false;
        return;
      }

      const sessionId = trialClass._id;

      const callTokenResponse = await CometChatCalls.generateToken(sessionId);
      const callToken =
        typeof callTokenResponse === "string"
          ? callTokenResponse
          : (callTokenResponse as { token?: string })?.token;

      if (!callToken || typeof callToken !== "string") {
        throw new Error("Failed to generate valid call token");
      }

      clearEventListeners();
      addCallsListener("onLeaveSessionButtonClicked", () => {
        endAndClose();
      });
      addCallsListener("onCallEndButtonPressed", () => {
        endAndClose();
      });
      addCallsListener("onSessionLeft", () => {
        endAndClose();
      });
      addCallsListener("onConnectionClosed", () => {
        endAndClose();
      });
      addCallsListener("onSessionTimedOut", () => {
        endAndClose();
      });

      const sessionSettings = {
        defaultLayout: true,
        isAudioOnly: false,
        isAudioCall: false,
        ShowPauseVideoButton: true,
        StartAudioMuted: false,
        StartVideoMuted: false,
      };

      if (!callContainerRef.current) {
        setError("Call container not available");
        setIsStarting(false);
        hasStartedRef.current = false;
        return;
      }

      console.log("Joining CometChat session with container:", callContainerRef.current);
      await CometChatCalls.joinSession(
        callToken,
        sessionSettings,
        callContainerRef.current
      );
      setIsStarting(false);
    } catch (startError) {
      console.error("Error starting call session:", startError);
      setError("Failed to start call session");
      setIsStarting(false);
      hasStartedRef.current = false;
    }
  }, [trialClass?._id, endAndClose, clearEventListeners]);

  const setCallContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      callContainerRef.current = element;
      containerReadyRef.current = !!element;
      console.log("Container ref set:", element);

      if (element && isOpen && trialClass?._id && !hasStartedRef.current) {
        setTimeout(() => {
          if (callContainerRef.current && !hasStartedRef.current) {
            void startCallSession();
          }
        }, 200);
      }
    },
    [isOpen, trialClass?._id, startCallSession]
  );

  const handleDialogEntered = useCallback(() => {
    console.log("Dialog entered, checking container...");
    if (isOpen && trialClass?._id && callContainerRef.current && !hasStartedRef.current) {
      setTimeout(() => {
        if (callContainerRef.current && !hasStartedRef.current) {
          void startCallSession();
        }
      }, 100);
    }
  }, [isOpen, trialClass?._id, startCallSession]);

  useEffect(() => {
    if (!isOpen) {
      isClosingRef.current = false;
      cleanupSession();
    }
  }, [isOpen, cleanupSession]);

  return (
    <Dialog
      open={isOpen}
      onClose={endAndClose}
      fullScreen
      TransitionProps={{
        onEntered: handleDialogEntered,
      }}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          m: 0,
          borderRadius: 0,
          bgcolor: "#000",
        },
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "#000",
          height: "100%",
        }}
      >
        {isStarting && (
          <div className="absolute inset-0 flex justify-center items-center bg-gray-900 z-10">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Starting conference...</p>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex justify-center items-center bg-gray-900 z-10">
            <div className="text-center text-white">
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}
        <div
          ref={setCallContainerRef}
          className="w-full h-full flex-1"
          style={{ minHeight: 0 }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConferenceModal;
